import { parseDate, formatDate } from './dates';

export const DEPENDENT_WAITING_DAYS = 90;

const MEMBER_STATUS_REASONS = {
  Draft: 'the registration was never completed, so cover has not started',
  New: 'no premium has been paid yet, so cover has not started',
  Pending: 'no premium has been paid yet, so cover has not started',
  Dormant: 'premiums are behind (up to 3 months), so cover has lapsed until they pay',
  Defaulted: 'premiums are more than 3 months behind, so they are out of cover',
  Inactive: 'the membership has been withdrawn',
  Deceased: 'the member is already recorded as deceased; check for an existing principal claim',
};

const fullName = (p) =>
  [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(' ');

/** Why a claim can't be raised under this principal, or null. */
export const principalProblem = (m) => {
  if (!m) return null;
  if (m.status !== 'Active') {
    const reason = MEMBER_STATUS_REASONS[m.status] || "the member's status is unknown";
    return {
      title: `Member is ${m.status || 'not Active'}`,
      text: `${fullName(m)} is ${m.status} (${reason}). Claims can only be raised for Active members.`,
    };
  }
  if (!m.policyMatured) {
    return {
      title: 'Policy not matured',
      text: `${fullName(m)}'s policy has not matured. It matures after 3 consecutive paid months and 90 days from the first of those payments.`,
    };
  }
  return null;
};

/** Why this dependent can't be claimed for, or null. The principal row (no dependentId) is skipped. */
export const dependentProblem = (d) => {
  if (!d?.dependentId) return null;
  if (d.status === 'Deceased') {
    return {
      title: 'Already recorded as deceased',
      text: `${fullName(d)} is already recorded as deceased. Check for an existing claim on this member.`,
    };
  }
  if (d.status && d.status !== 'Active') {
    const added = parseDate(d.insertDate);
    const from = added ? added.clone().add(DEPENDENT_WAITING_DAYS, 'days') : null;
    const waiting = from && from.isAfter(new Date());
    return {
      title: `Dependent is ${d.status}`,
      text: waiting
        ? `${fullName(d)} was added on ${formatDate(added)} and is still in the ${DEPENDENT_WAITING_DAYS}-day waiting period. They are covered from ${formatDate(from)}.`
        : `${fullName(d)}'s dependent record is ${d.status}, so they are not covered (for example a child added at 18 or over). Check the dependent record first.`,
    };
  }
  return null;
};

/** Waiting period counted to the date of death, or null when served. */
export const waitingPeriodProblem = (d, deathDate) => {
  if (!d?.dependentId || !deathDate) return null;
  const added = parseDate(d.insertDate);
  const died = parseDate(deathDate);
  if (!added || !died) return null;
  const days = died.clone().startOf('day').diff(added.clone().startOf('day'), 'days');
  if (days < 0) {
    return {
      title: 'Added after death',
      text: `${fullName(d)} was added as a dependent on ${formatDate(added)}, after their date of death (${formatDate(died)}). A dependent added after death is not covered.`,
    };
  }
  if (days < DEPENDENT_WAITING_DAYS) {
    return {
      title: 'Within the waiting period',
      text: `${fullName(d)} died on ${formatDate(died)}, ${days} days after being added on ${formatDate(added)}. Dependents are covered only after a ${DEPENDENT_WAITING_DAYS}-day waiting period (from ${formatDate(added.clone().add(DEPENDENT_WAITING_DAYS, 'days'))}).`,
    };
  }
  return null;
};

/** Splits a backend "Claim not allowed: …" message into a popup title and text. */
export const ruleFailure = (message, fallbackTitle) => {
  const prefix = 'Claim not allowed:';
  if (message && message.startsWith(prefix)) {
    const text = message.slice(prefix.length).trim();
    return { title: 'Claim not allowed', text: text.charAt(0).toUpperCase() + text.slice(1) };
  }
  return { title: fallbackTitle, text: message };
};
